"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useQuery } from "@tanstack/react-query";
import { getCurrentUser } from "@/lib/auth";
import { checkoutClubDues, formatApiError } from "@/lib/api";
import { StripeCardPayment } from "@/components/payments/StripeCardPayment";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { ClubDuesCheckout } from "@/types/club";

export default function ClubDuesPay({
  publishableKey,
  currency,
}: {
  publishableKey?: string;
  currency?: string;
}) {
  const [email, setEmail] = useState("");
  const [session, setSession] = useState<ClubDuesCheckout | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const user = useQuery({ queryKey: ["club-user"], queryFn: getCurrentUser });
  useEffect(() => {
    if (user.data) setEmail(user.data.email);
  }, [user.data]);

  const start = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setLoading(true);
    try {
      setSession(await checkoutClubDues(email.trim()));
    } catch (cause) {
      setError(formatApiError(cause, "No outstanding dues were found for that email."));
    } finally {
      setLoading(false);
    }
  };

  if (user.isLoading) return <p role="status">Loading account...</p>;
  if (!user.data) return (
    <Link href="/auth/magic-link" className="mt-6 inline-flex min-h-[44px] items-center underline">
      Sign in by email to pay your dues
    </Link>
  );

  if (done) {
    return (
      <p className="mt-6 rounded border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
        Dues payment received. Thank you.
      </p>
    );
  }

  if (session) {
    return (
      <div className="mt-6 max-w-lg">
        <h3 className="font-semibold">Pay dues for {session.memberName}</h3>
        <StripeCardPayment
          clientSecret={session.clientSecret}
          publishableKey={session.publishableKey || publishableKey || ""}
          amount={Number(session.amount)}
          currency={session.currency || currency}
          collectContact={false}
          defaultEmail={email}
          onSucceeded={() => setDone(true)}
        />
      </div>
    );
  }

  return (
    <form onSubmit={start} className="mt-6 max-w-md space-y-3">
      <div>
        <Label htmlFor="duesEmail">Member email</Label>
        <Input
          id="duesEmail"
          type="email"
          required
          readOnly
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
      </div>
      {error && (
        <div role="alert" className="text-sm text-red-700">
          {error}
          <Link href="/auth/magic-link" className="block min-h-[44px] py-3 underline">Verify your email</Link>
        </div>
      )}
      <Button type="submit" disabled={loading || !publishableKey}>
        {loading ? "Looking up…" : "Pay outstanding dues"}
      </Button>
      {!publishableKey && (
        <p className="text-xs text-slate-500">Online payment is not configured yet.</p>
      )}
    </form>
  );
}
